import type { SupabaseClient } from '../client'
import { SupabaseError } from '../errors'

/**
 * Os fornecedores de um projeto.
 *
 * São os terceiros que podem ser donos de um conserto: a agência do cliente, o
 * sistema de gestão dele, o provedor de hospedagem. São só um nome com contato
 * opcional, sem acesso ao portal e sem notificação: quem fala com eles é o
 * cliente, fora daqui.
 *
 * As duas funções passam por RPC porque a RLS de `portal.executores` é da
 * equipe, e o cliente entra por elas, que checam o acesso ao projeto.
 */

export type ExecutorRow = {
  id: string
  nome: string
  contato: string | null
  /** `cliente` ou `equipe`: os dois lados cadastram, e a lista diz quem foi. */
  quem_criou: 'cliente' | 'equipe'
  created_at: string
}

export async function listExecutores(
  client: SupabaseClient,
  projectId: number,
): Promise<ExecutorRow[]> {
  const portal = client.schema('portal')
  const { data, error } = await portal.rpc('listar_executores', { p_project_id: projectId })

  if (error) throw new SupabaseError(error.message, error)
  return (data ?? []) as ExecutorRow[]
}

/**
 * Cadastra um fornecedor.
 *
 * Nome repetido no mesmo projeto devolve o que já existe, em vez de erro: para
 * quem está cadastrando no meio de um chamado, o efeito desejado é o mesmo, e
 * um erro ali só interromperia o relato.
 */
export async function cadastrarExecutor(
  client: SupabaseClient,
  projectId: number,
  nome: string,
  contato?: string | null,
): Promise<ExecutorRow> {
  const portal = client.schema('portal')
  const { data, error } = await portal.rpc('cadastrar_executor', {
    p_project_id: projectId,
    p_nome: nome,
    // a RPC declara o parâmetro com DEFAULT, então o tipo gerado é opcional e
    // não aceita null: omitir é o que faz o default valer
    p_contato: contato ?? undefined,
  })

  if (error) throw new SupabaseError(error.message, error)

  // a RPC de cadastro devolve a linha crua da tabela, que guarda os dois
  // criadores possíveis; `quem_criou` é a leitura disso, e só a RPC de listagem
  // já entrega pronta. Derivar aqui evita o cliente ter que recarregar a lista
  // só para descobrir quem cadastrou o que ele mesmo acabou de cadastrar.
  return {
    id: data.id,
    nome: data.nome,
    contato: data.contato,
    quem_criou: data.criado_por_usuario ? 'equipe' : 'cliente',
    created_at: data.created_at,
  }
}
